import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import { cancelSubscriptionAdminCallable } from '@/lib/callables';
import { SUBSCRIPTION_CANCEL_REASON_MAX, type TenantId } from '@etiquetachef/types';
import { gatewayErrorMessage } from './gatewayErrors';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  onClose: () => void;
};

/**
 * Cancela a assinatura do tenant (super-admin) no gateway (Stripe ou Asaas), no
 * fim do período pago: o cliente fica até lá e o aviso do gateway corta o
 * acesso (#961). Só sem período a preservar (já encerrada no gateway) o corte é
 * imediato, com o paywall (`/select-plan`).
 *
 * Origem: sem issue — commit e7128a53d, 2026-06-09 22:21 +0200.
 *
 * Contrato: chama `cancelSubscriptionAdmin`. Se o gateway não confirmar, o
 * servidor devolve `unavailable` com `details.code: gateway_cancel_unconfirmed`
 * SEM escrever nada (#795, BIL-5); a mensagem traduzida diz que a assinatura
 * continua ativa e que dá para tentar de novo. O toast diz até quando o
 * cliente tem acesso (`scheduledUntil`) ou que o corte foi imediato.
 *
 * O servidor exige `ADMIN_PERMISSIONS.manageSubscriptions` (ROOT/FINANCE,
 * `apps/functions/src/auth/permissions.ts`; staff only).
 *
 * Usado por: `BillingActions` → `routes/TenantDetailPage.tsx` (rota
 * `/tenants/:id`, menu Tenants), admin web. Spec:
 * `docs/screens/tenant-detail-admin.md`.
 *
 * Histórico:
 *  - 2026-10-03 18:22 +0200 — (#795, BIL-5) recusa `gateway_cancel_unconfirmed`
 *    traduzida pelo `details.code`.
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu e permissão no cabeçalho (#796).
 *  - 2026-10-13 16:52 +0200 — (#961) cancelamento no fim do período pago;
 *    textos e toast com a data do fim do acesso.
 *  - 2026-10-13 17:35 +0200 — (#961) motivo limitado a
 *    `SUBSCRIPTION_CANCEL_REASON_MAX` (120) no campo, com contador; o servidor
 *    recusa acima disso. Antes o campo aceitava 500 e o texto era cortado.
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function CancelSubscriptionDialog({
  open,
  tenantId,
  tenantName,
  onClose,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close(): void {
    setReason('');
    setError(null);
    onClose();
  }

  /**
   * Envia o cancelamento ao `cancelSubscriptionAdmin` e fecha com toast de sucesso.
   *
   * Origem: sem issue — commit e7128a53d, 2026-06-09 22:21 +0200.
   *
   * Contrato: motivo vazio → erro local, sem rede. Input
   * `{ "tenantId": "tenant-1", "reason": "..." }`. Erro → `gatewayErrorMessage`
   * (`unavailable` + `gateway_cancel_unconfirmed`: nada foi gravado, a
   * assinatura segue ativa) ou `err.message`, num `Alert` dentro do modal (#1021).
   *
   * Usado por: botão "Confirmar" deste diálogo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   */
  async function handleSubmit(): Promise<void> {
    if (reason.trim().length === 0) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await cancelSubscriptionAdminCallable({ tenantId, reason: reason.trim() });
      // #961: agendado → o cliente segue com acesso até a data; senão, corte imediato.
      toast.success(
        result.scheduledUntil !== null
          ? t('billing.cancelSubscription.success', {
              date: new Date(result.scheduledUntil).toLocaleDateString(i18n.language),
            })
          : t('billing.cancelSubscription.successImmediate'),
      );
      close();
    } catch (err) {
      // #795 (BIL-5, 2026-10-03 18:22 +0200): `unavailable` +
      // `gateway_cancel_unconfirmed` = nada mudou, a assinatura segue cobrando.
      // Sem a tradução o operador via o texto cru do servidor, em português.
      setError(
        gatewayErrorMessage(err, t) ??
          (err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric')),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('billing.cancelSubscription.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
            >
              {t('billing.cancelSubscription.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('billing.cancelSubscription.description', { name: tenantName })}
        </Box>
        <Alert type="warning">{t('billing.cancelSubscription.warning')}</Alert>
        {/* #961 — o motivo vai para `cancelled_reason`, que guarda 120
            caracteres; o campo para no limite e mostra quanto falta, em vez de
            o servidor cortar o texto em silêncio. */}
        <FormField
          label={t('tenantDetail.actions.reasonLabel')}
          constraintText={`${String(reason.length)}/${String(SUBSCRIPTION_CANCEL_REASON_MAX)}`}
        >
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value.slice(0, SUBSCRIPTION_CANCEL_REASON_MAX));
              // #1021 (2026-10-25 15:00 +0100): digitar o motivo tira o "motivo obrigatório" do Alert.
              setError(null);
            }}
            rows={3}
            disabled={submitting}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
